import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Question, X, WhatsappLogo, WarningCircle, BookOpen, Heart, CaretRight,
  ShieldCheck, InstagramLogo, DiscordLogo, FacebookLogo, YoutubeLogo,
} from '@phosphor-icons/react';
import { usePlatform } from '../context/PlatformContext';

export default function HelpFAB() {
  const [isOpen, setIsOpen] = useState(false);
  const [hiddenByModal, setHiddenByModal] = useState(false);
  const { platformConfig } = usePlatform();

  useEffect(() => {
    const checkModal = () => {
      const modalActive = !!document.querySelector('[role="dialog"]');
      setHiddenByModal(modalActive);
    };
    const observer = new MutationObserver(checkModal);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const sections = [
    {
      title: "Bantuan Cepat",
      items: [
        { label: "Hub Kendala & Error", sub: "Emergency Support", icon: WarningCircle, color: "bg-red-400", href: "mailto:support@traktirkan.com", isExternal: true },
        platformConfig?.whatsapp_url && { label: "Chat WhatsApp Admin", sub: "Support 1-6 Jam", icon: WhatsappLogo, color: "bg-green-400", href: platformConfig.whatsapp_url, isExternal: true },
      ].filter(Boolean)
    },
    {
      title: "Komunitas & Sosial Media",
      items: [
        platformConfig?.instagram_url && { label: "Instagram", sub: "@traktirkan", icon: InstagramLogo, color: "bg-pink-400", href: platformConfig.instagram_url, isExternal: true },
        platformConfig?.discord_url && { label: "Discord Server", sub: "Gabung Komunitas", icon: DiscordLogo, color: "bg-indigo-400", href: platformConfig.discord_url, isExternal: true },
        platformConfig?.youtube_url && { label: "YouTube", sub: "Tutorial & Update", icon: YoutubeLogo, color: "bg-red-500", href: platformConfig.youtube_url, isExternal: true },
        platformConfig?.facebook_url && { label: "Facebook", sub: "Halaman Resmi", icon: FacebookLogo, color: "bg-blue-500", href: platformConfig.facebook_url, isExternal: true },
      ].filter(Boolean)
    },
    {
      title: "Dukung Pengembangan",
      items: [
        { label: "Donasi Maintenance", sub: "Kirim Dukungan Anda", icon: Heart, color: "bg-orange-400", href: "/u/hasbi", isExternal: false },
      ].filter(Boolean)
    },
    {
      title: "Dokumentasi",
      items: [
        { label: "Halaman FAQ", sub: "Tanya Jawab Umum", icon: Question, color: "bg-purple-400", href: "/faq", isExternal: false },
        { label: "Panduan Pengguna", sub: "Buku Dokumentasi", icon: BookOpen, color: "bg-yellow-400", href: "/panduan", isExternal: false },
      ].filter(Boolean)
    }
  ].filter(sec => sec.items.length > 0);

  return (
    <>
      {/* OVERLAY / MODAL */}
      <div className={`fixed inset-0 z-[60] flex items-end justify-end p-6 transition-all duration-300 ${isOpen ? 'opacity-100 backdrop-blur-sm pointer-events-auto' : 'opacity-0 pointer-events-none invisible'}`}>
        <div className={`w-full max-w-[400px] bg-background border-4 border-foreground shadow-[12px_12px_0px_0px_#000] p-6 max-h-[85vh] overflow-y-auto transition-all duration-300 transform ${isOpen ? 'translate-y-0 scale-100' : 'translate-y-10 scale-95'}`}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight italic uppercase">
                PUSAT <span className="text-primary italic">BANTUAN</span>
              </h2>
              <p className="text-xs font-bold text-foreground/60 uppercase tracking-widest mt-1">Apa yang bisa kami bantu hari ini?</p>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="w-10 h-10 border-2 border-foreground bg-white flex items-center justify-center shadow-brutal-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
            >
              <X size={20} weight="bold" />
            </button>
          </div>

          <div className="space-y-8">
            {sections.map((sec, idx) => (
              <div key={idx}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-px bg-foreground/20 flex-1" />
                  <p className="text-[10px] font-black uppercase tracking-[0.3em] text-foreground/40">{sec.title}</p>
                  <div className="h-px bg-foreground/20 flex-1" />
                </div>
                <div className="grid gap-3">
                  {sec.items.map((item, i) => {
                    const content = (
                      <>
                        <div className={`w-12 h-12 border-2 border-foreground flex items-center justify-center ${item.color}`}>
                          <item.icon size={24} weight="bold" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm leading-tight">{item.label}</p>
                          <p className="text-[10px] uppercase font-bold text-foreground/50 tracking-wide">{item.sub}</p>
                        </div>
                        <CaretRight size={16} weight="bold" className="text-foreground/30 group-hover:text-foreground transition-colors" />
                      </>
                    );
                    const classes = "group flex items-center gap-4 border-2 border-foreground bg-white p-3 shadow-brutal-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all";

                    return item.isExternal ? (
                      <a key={i} href={item.href} target="_blank" rel="noreferrer" className={classes}>
                        {content}
                      </a>
                    ) : (
                      <Link key={i} to={item.href} onClick={() => setIsOpen(false)} className={classes}>
                        {content}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t-2 border-foreground/10 text-center">
            <p className="text-[10px] font-bold text-foreground/40 uppercase tracking-widest">Traktirkan v1.3.0 — Dibuat untuk Kreator</p>
          </div>
        </div>
      </div>

      {/* FLOATING BUTTON */}
      {!hiddenByModal && (
        <>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="fixed bottom-6 right-6 z-[70] w-14 h-14 border-4 border-foreground bg-primary text-white flex items-center justify-center shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] active:scale-95 transition-all"
            aria-label="Bantuan"
          >
            {isOpen ? <X size={28} weight="bold" /> : <Question size={28} weight="bold" />}
          </button>

          {!isOpen && (
            <div className="fixed bottom-6 right-6 z-[69] w-14 h-14 pointer-events-none animate-ping opacity-20">
              <div className="w-full h-full bg-primary rounded-none" />
            </div>
          )}
        </>
      )}
    </>
  );
}
